import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, CalendarDays, Users, ArrowRight, Star } from "lucide-react";
import { scrollToId } from "@/lib/api";
import { QuoteSummary } from "@/components/QuoteSummary";
import { DateRangeField, nightsBetween } from "@/components/DateRangeField";

const Field = ({ label, children, testId, className = "" }) => (
  <label
    data-testid={testId}
    className={`flex min-w-0 flex-col gap-0.5 px-4 py-2.5 transition-colors duration-300 focus-within:bg-sand/60 ${className}`}
  >
    <span className="text-[11px] font-semibold text-ink/80">{label}</span>
    {children}
  </label>
);

const inputCls = "block w-full min-w-0 min-h-[24px] bg-transparent text-[15px] text-ink outline-none placeholder:text-fog/60";

export const Hero = ({ property, booking, setBooking, quoteState, booked = [], bookedMatchers = [] }) => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 180]);
  const scale = useTransform(scrollY, [0, 900], [1, 1.12]);

  return (
    <section data-testid="hero-section" className="relative pt-20 sm:pt-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.985 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="grain relative h-[62svh] min-h-[440px] overflow-hidden rounded-[28px] sm:rounded-[36px] md:h-[78svh] md:min-h-[560px]"
          >
            <motion.div style={{ y, scale }} className="absolute inset-0">
              <img
                src={property?.hero_image}
                alt="Lisboa"
                className="h-full w-full object-cover"
                data-testid="hero-background-image"
              />
            </motion.div>
            <div className="absolute inset-0 bg-ink/25" />

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="absolute left-5 top-5 flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-xs font-semibold text-ink backdrop-blur-md sm:left-7 sm:top-7"
              data-testid="hero-eyebrow"
            >
              <MapPin size={13} className="text-clay" />
              {property?.location} · {property?.type}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="absolute right-5 top-5 hidden items-center gap-1.5 rounded-full bg-white/90 px-4 py-2 text-xs font-semibold text-ink backdrop-blur-md sm:right-7 sm:top-7 sm:flex"
              data-testid="hero-rating-pill"
            >
              <Star size={12} className="fill-clay text-clay" />
              {property?.price_per_night ? `${property.price_per_night} € / noite` : "Anfitriã local"}
            </motion.p>
          </motion.div>

          <div className="relative z-10 mx-auto -mt-28 w-full max-w-[900px] px-2 sm:px-4 md:-mt-40">
            <motion.div
              initial={{ opacity: 0, y: 36 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="w-full rounded-3xl bg-white p-5 shadow-[0_30px_80px_-24px_rgba(28,27,26,0.45)] sm:p-7 md:p-8"
              data-testid="hero-booking-card"
            >
              <h1
                className="font-display text-[1.75rem] font-extrabold leading-[1.08] tracking-[-0.035em] text-ink sm:text-[2.2rem] md:text-[2.5rem]"
                data-testid="hero-title"
              >
                Um refúgio luminoso às portas de Lisboa.
              </h1>
              {property?.price_per_night && (
                <p className="mt-3 flex items-baseline gap-1.5" data-testid="hero-price">
                  <span className="font-display text-3xl font-extrabold text-clay">{property.price_per_night} €</span>
                  <span className="text-sm font-medium text-fog">/ noite · {property?.address}</span>
                </p>
              )}
              <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-fog" data-testid="hero-subtitle">
                {property?.tagline}. {property?.bedrooms} quartos, {property?.bathrooms} casas de banho e terraço amplo — até {property?.max_guests} hóspedes.
              </p>

              <div className="mt-6 grid gap-2.5 md:grid-cols-[1fr_2.1fr_1.1fr_auto] md:items-stretch">
                <div className="overflow-hidden rounded-xl border border-ink/20">
                  <Field label="Localização" testId="booking-field-location" className="h-full">
                    <span className="flex items-center gap-2 text-[15px] text-ink">{property?.location || "Queluz, Lisboa"}</span>
                  </Field>
                </div>
                <div
                  className="overflow-hidden rounded-xl border border-ink/20 transition-colors duration-300 hover:border-clay"
                  data-testid="booking-field-dates"
                >
                  <DateRangeField
                    booked={booked}
                    bookedMatchers={bookedMatchers}
                    value={{ checkIn: booking.checkIn, checkOut: booking.checkOut }}
                    onChange={(range) => setBooking((b) => ({ ...b, ...range }))}
                    nights={nightsBetween(booking.checkIn, booking.checkOut)}
                    testId="hero-dates"
                    triggerClassName="h-full px-4 py-2.5"
                  />
                </div>
                <div className="overflow-hidden rounded-xl border border-ink/20">
                  <Field label="Hóspedes" testId="booking-field-guests" className="h-full">
                    <span className="flex items-center gap-2">
                      <Users size={15} className="text-fog" />
                      <select
                        data-testid="booking-guests-select"
                        value={booking.guests}
                        onChange={(e) => setBooking((b) => ({ ...b, guests: Number(e.target.value) }))}
                        className={inputCls}
                      >
                        {Array.from({ length: property?.max_guests || 6 }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={n}>{n} {n === 1 ? "hóspede" : "hóspedes"}</option>
                        ))}
                      </select>
                    </span>
                  </Field>
                </div>
                <button
                  data-testid="booking-search-btn"
                  onClick={() => scrollToId("reservar")}
                  className="group mt-1 flex w-full items-center justify-center gap-2 rounded-xl bg-clay px-6 py-4 text-[15px] font-bold text-white transition-[transform,background-color] duration-300 hover:scale-[1.015] hover:bg-clay-dark md:mt-0 md:h-full"
                >
                  <CalendarDays size={16} />
                  Reservar
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
              {(booking.checkIn && booking.checkOut) && (
                <div className="mt-3" data-testid="hero-quote">
                  <QuoteSummary {...quoteState} testId="hero-quote" />
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
